import { useCallback, useRef, useState } from "react";

export type ScratchPoint = { x: number; y: number };
export type NotebookShape =
  | { tool: "pen" | "erase"; points: ScratchPoint[]; width: number }
  | { tool: "line" | "ellipse"; from: ScratchPoint; to: ScratchPoint }
  | { tool: "text"; at: ScratchPoint; value: string };

export type ColumnDraft = {
  main: string[][];
  carry: string[][];
  rules: number[];
};

export type BoardDraft = {
  // JSON-safe Excalidraw scene. Kept structural here so the heavy board remains
  // behind Scratch's dynamic import and never enters the initial bundle.
  elements: readonly unknown[];
};

export type ScratchDraft = {
  mode: "hand" | "column" | "board";
  notebook: NotebookShape[];
  column: ColumnDraft | null;
  board: BoardDraft | null;
};

export type TaskDraft = {
  raw: string;
  scratch: ScratchDraft;
  updatedAt: number;
};

type DraftStore = {
  version: 1;
  drafts: Record<string, TaskDraft>;
};

const STORAGE_KEY = "traektoria.task-drafts.v1";
const MAX_DRAFTS = 8;
const memoryDrafts = new Map<string, TaskDraft>();
const pendingWrites = new Map<string, ReturnType<typeof setTimeout>>();

function remember(itemKey: string, draft: TaskDraft): void {
  memoryDrafts.set(itemKey, draft);
  if (memoryDrafts.size <= MAX_DRAFTS) return;
  const oldest = [...memoryDrafts.entries()].sort(([, a], [, b]) => a.updatedAt - b.updatedAt)[0];
  if (oldest !== undefined) memoryDrafts.delete(oldest[0]);
}

export function emptyScratchDraft(): ScratchDraft {
  return { mode: "hand", notebook: [], column: null, board: null };
}

export function emptyTaskDraft(): TaskDraft {
  return { raw: "", scratch: emptyScratchDraft(), updatedAt: 0 };
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readStore(target: Storage | null = storage()): DraftStore {
  if (target === null) return { version: 1, drafts: {} };
  try {
    const parsed: unknown = JSON.parse(target.getItem(STORAGE_KEY) ?? "null");
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "version" in parsed &&
      parsed.version === 1 &&
      "drafts" in parsed &&
      typeof parsed.drafts === "object" &&
      parsed.drafts !== null
    ) {
      return parsed as DraftStore;
    }
  } catch {
    // A damaged or blocked session store must not stop the task screen.
  }
  return { version: 1, drafts: {} };
}

function writeStore(store: DraftStore, target: Storage | null = storage()): boolean {
  if (target === null) return false;
  try {
    target.setItem(STORAGE_KEY, JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
}

export function readTaskDraft(itemKey: string, target?: Storage | null): TaskDraft {
  if (target === undefined) {
    const memory = memoryDrafts.get(itemKey);
    if (memory !== undefined) return memory;
  }
  const stored = readStore(target).drafts[itemKey];
  if (target === undefined && stored !== undefined) remember(itemKey, stored);
  return stored ?? emptyTaskDraft();
}

export function writeTaskDraft(
  itemKey: string,
  draft: TaskDraft,
  target?: Storage | null,
): boolean {
  if (target === undefined) remember(itemKey, draft);
  const store = readStore(target);
  const drafts = { ...store.drafts, [itemKey]: draft };
  const keep = Object.entries(drafts)
    .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_DRAFTS);
  return writeStore({ version: 1, drafts: Object.fromEntries(keep) }, target);
}

function scheduleTaskDraft(itemKey: string, draft: TaskDraft): void {
  remember(itemKey, draft);
  const pending = pendingWrites.get(itemKey);
  if (pending !== undefined) clearTimeout(pending);
  pendingWrites.set(
    itemKey,
    setTimeout(() => {
      pendingWrites.delete(itemKey);
      writeTaskDraft(itemKey, draft);
    }, 120),
  );
}

export function clearTaskDraft(itemKey: string, target?: Storage | null): boolean {
  if (target === undefined) memoryDrafts.delete(itemKey);
  const pending = pendingWrites.get(itemKey);
  if (pending !== undefined) {
    clearTimeout(pending);
    pendingWrites.delete(itemKey);
  }
  const store = readStore(target);
  if (!(itemKey in store.drafts)) return true;
  const drafts = { ...store.drafts };
  delete drafts[itemKey];
  return writeStore({ version: 1, drafts }, target);
}

export function useTaskDraft(itemKey: string) {
  const [entry, setEntry] = useState(() => ({ itemKey, draft: readTaskDraft(itemKey) }));
  const active = useRef({ itemKey, value: true });
  if (active.current.itemKey !== itemKey) active.current = { itemKey, value: true };
  const draft = entry.itemKey === itemKey ? entry.draft : readTaskDraft(itemKey);

  const update = useCallback(
    (change: (current: TaskDraft) => TaskDraft): void => {
      setEntry((currentEntry) => {
        if (!active.current.value) return currentEntry;
        const current =
          currentEntry.itemKey === itemKey ? currentEntry.draft : readTaskDraft(itemKey);
        const next = { ...change(current), updatedAt: Date.now() };
        scheduleTaskDraft(itemKey, next);
        return { itemKey, draft: next };
      });
    },
    [itemKey],
  );

  const clear = useCallback((): void => {
    active.current.value = false;
    clearTaskDraft(itemKey);
  }, [itemKey]);

  return { draft, update, clear };
}
